import React, { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import { useCookieConsent } from "../contexts/CookieConsentContext";

const A = {
  bg:      "#FBFBF9",
  surface: "#EEF2F2",
  card:    "#FFFFFF",
  crimson: "#1F7D91",
  gold:    "#E3704F",
  dark:    "#18211C",
  muted:   "#5D635F",
  border:  "#E6E5E1",
};

const CATEGORIES = [
  {
    key: "necessary",
    title: "Strictly Necessary",
    locked: true,
    description:
      "Required for the site to function: authentication, session, CSRF protection, " +
      "and payment processor security cookies. These cannot be disabled.",
  },
  {
    key: "functional",
    title: "Functional",
    description:
      "Remember your preferences (theme, language, last-viewed dashboard). " +
      "Disabling these will reset preferences on each visit.",
  },
  {
    key: "analytics",
    title: "Analytics",
    description:
      "Help us understand how the product is used so we can improve it. " +
      "No analytics providers are deployed today; this toggle will gate them if added.",
  },
  {
    key: "marketing",
    title: "Marketing",
    description:
      "Used to measure marketing campaigns and personalize content. " +
      "No marketing trackers are deployed today; this toggle will gate them if added.",
  },
];

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        width: 44,
        height: 24,
        borderRadius: 999,
        border: "none",
        background: checked ? A.crimson : "#C9D5D3",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        position: "relative",
        transition: "background-color 180ms",
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
          transition: "left 180ms",
        }}
      />
    </button>
  );
}

export default function CookiePreferencesModal({ open, onClose }) {
  const { prefs, savePrefs } = useCookieConsent();
  const [draft, setDraft] = useState(prefs);

  useEffect(() => {
    if (open) setDraft(prefs);
  }, [open, prefs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const setKey = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    savePrefs(draft);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-prefs-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(24,33,28,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: A.card,
          border: `1px solid ${A.border}`,
          borderRadius: 12,
          maxWidth: 560,
          width: "100%",
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "0 20px 50px rgba(24,33,28,0.35)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1.25rem 1.5rem",
            borderBottom: `1px solid ${A.border}`,
            background: A.surface,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <h2
            id="cookie-prefs-title"
            style={{
              margin: 0,
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: "1.35rem",
              fontWeight: 700,
              color: A.dark,
            }}
          >
            Cookie Preferences
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: A.muted,
              padding: 4,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "1.5rem", color: A.dark, fontSize: "0.95rem", lineHeight: 1.55 }}>
          <p style={{ margin: "0 0 1.25rem", color: A.muted }}>
            Choose which cookies you allow. Strictly necessary cookies are always on so the
            site works. You can change these settings any time from the footer.
          </p>

          {CATEGORIES.map((c) => (
            <div
              key={c.key}
              style={{
                display: "flex",
                gap: 16,
                padding: "1rem 0",
                borderBottom: `1px solid ${A.border}`,
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: A.dark, marginBottom: 4 }}>{c.title}</div>
                <div style={{ fontSize: "0.85rem", color: A.muted, lineHeight: 1.5 }}>{c.description}</div>
              </div>
              <Toggle
                checked={c.locked ? true : !!draft[c.key]}
                onChange={(v) => setKey(c.key, v)}
                disabled={c.locked}
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "1rem 1.5rem 1.25rem",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            flexWrap: "wrap",
            borderTop: `1px solid ${A.border}`,
            background: A.bg,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              color: A.dark,
              border: `1.5px solid ${A.border}`,
              padding: "0.55rem 1.2rem",
              borderRadius: 8,
              fontWeight: 600,
              fontSize: "0.85rem",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              background: A.crimson,
              color: "#FFFFFF",
              border: "none",
              padding: "0.6rem 1.3rem",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: "0.82rem",
              cursor: "pointer",
              letterSpacing: "0.05em",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "inherit",
            }}
          >
            <Check size={16} />
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
